import React, { FC, useEffect, useState, useMemo, useContext } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { ConfigContext, Language, PostBillAPIContext } from "@app/provider";
import { SpinnerView } from "@app/components";
import { routes } from "@app/routing";
import { logMsg } from "@app/utils/logMsg";

import BillingUpdatePaymentForm from "../billingUpdatePaymentForm";

const STRIPE_APPEARANCE = {
  rules: {
    ".TermsText": {
      fontFamily: '"Roboto Flex", sans-serif',
      fontSize: "12px",
      lineHeight: "18px",
      letterSpacing: "0.005em",
      color: "#0000008F",
      fontWeight: "400",
    },
  },
};

const BillingUpdatePaymentContainer: FC = () => {
  const { config } = useContext(ConfigContext);
  const { updatePayment } = useContext(PostBillAPIContext);

  const navigate = useNavigate();
  const { locale } = useIntl();

  const [clientSecret, setClientSecret] = useState<string>("");
  const [customerSessionSecret, setCustomerSessionSecret] =
    useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const currentLocale = locale === "es" ? Language.ES : Language.EN;
  const publishableKey = config.preBill.postBillStripeKey;

  const stripePromise = useMemo(
    () => loadStripe(publishableKey, { locale: currentLocale }),
    [publishableKey, currentLocale]
  );

  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      customerSessionClientSecret: customerSessionSecret,
      appearance: STRIPE_APPEARANCE,
    }),
    [clientSecret, customerSessionSecret]
  );

  const redirectToBillingCenter = (): void => {
    navigate(routes.billingCenter, {
      state: { from: routes.billingUpdatePayment },
    });
  };

  const handleUpdatePayment = async (): Promise<void> => {
    try {
      const response = await updatePayment();
      const {
        clientSecret: newClientSecret,
        customerSessionSecret: newCustomerSessionSecret,
      } = response.data || {};

      setClientSecret(newClientSecret || "");
      setCustomerSessionSecret(newCustomerSessionSecret || "");
    } catch {
      redirectToBillingCenter();
      logMsg("error", "postbill.payment", "Error updating payment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleUpdatePayment();
  }, []);

  return (
    <>
      <SpinnerView isLoading={loading} />
      {clientSecret && (
        <Elements
          stripe={stripePromise}
          options={elementsOptions}
          key={currentLocale}
        >
          <BillingUpdatePaymentForm />
        </Elements>
      )}
    </>
  );
};

export default BillingUpdatePaymentContainer;
